import type { JsonObject, JsonValue } from "./json";

/** Describes out-of-band resource bytes without adding fields to the native body. */
export const INPUT_RESOURCE_SCHEMA_KEYWORD = { keyword: "x-resource", schemaType: "object", valid: true } as const;

export type InputResourceLimit = Readonly<{
  uriProperty: string;
  mimeTypeProperty: string;
  mimeTypePrefix: string;
  uriSchemes: readonly string[];
  maxBytes: number;
}>;

export type InputResourceReference = Readonly<{ uri: string; mimeType: string; maxBytes: number }>;

const object = (value: JsonValue | undefined): JsonObject => value !== null && typeof value === "object" && !Array.isArray(value) ? value : {};

function resourceLimit(schema: JsonObject): InputResourceLimit | undefined {
  const value = object(schema["x-resource"]);
  if (typeof value.uriProperty !== "string" || typeof value.mimeTypeProperty !== "string"
    || typeof value.mimeTypePrefix !== "string" || !Array.isArray(value.uriSchemes)
    || !value.uriSchemes.every(scheme => typeof scheme === "string")
    || typeof value.maxBytes !== "number" || !Number.isSafeInteger(value.maxBytes) || value.maxBytes < 1) return;
  return value as unknown as InputResourceLimit;
}

function visitSchema(schema: JsonObject, visit: (schema: JsonObject) => void): void {
  visit(schema);
  for (const child of Object.values(object(schema.properties))) visitSchema(object(child), visit);
  if (schema.items) visitSchema(object(schema.items), visit);
}

/** A local Blob uploaded as HTTP uses the same limit as its eventual native reference. */
export function modelInputResourceMaxBytes(schema: JsonObject, mimeType: string, uriScheme: string): number | undefined {
  let maximum: number | undefined;
  visitSchema(schema, node => {
    const limit = resourceLimit(node);
    if (limit && mimeType.toLowerCase().startsWith(limit.mimeTypePrefix) && limit.uriSchemes.includes(uriScheme)) {
      maximum = Math.min(maximum ?? limit.maxBytes, limit.maxBytes);
    }
  });
  return maximum;
}

/** Read references through their authored schema paths; never rewrite the wire body. */
export function modelInputResourceReferences(schema: JsonObject, input: JsonObject): InputResourceReference[] {
  const result: InputResourceReference[] = [];
  const visit = (node: JsonObject, value: JsonValue | undefined): void => {
    const record = object(value);
    const limit = resourceLimit(node);
    if (limit) {
      const uri = record[limit.uriProperty];
      const mimeType = record[limit.mimeTypeProperty];
      if (typeof uri === "string" && typeof mimeType === "string" && mimeType.toLowerCase().startsWith(limit.mimeTypePrefix)) {
        try {
          if (limit.uriSchemes.includes(new URL(uri).protocol.slice(0, -1))) result.push({ uri, mimeType, maxBytes: limit.maxBytes });
        } catch { /* Malformed URIs remain the native contract validator's responsibility. */ }
      }
    }
    for (const [key, child] of Object.entries(object(node.properties))) if (key in record) visit(object(child), record[key]);
    if (Array.isArray(value) && node.items) for (const item of value) visit(object(node.items), item);
  };
  visit(schema, input);
  return result;
}
